import { Link, useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function FlowComplexityPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Managing Flow Complexity</h1>
          <p className="text-lg text-gray-600 mt-2">
            How to build maintainable flows that don't become unmanageable
          </p>
        </div>

        {/* Warning Alert */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Maintainability Warning</h3>
              <p className="mt-2 text-sm text-yellow-700">
                Overly complex flows are difficult to understand, troubleshoot, and modify. They're
                more prone to errors and take longer to debug when issues occur. Breaking complex
                flows into smaller, focused pieces improves reliability and team productivity.
              </p>
            </div>
          </div>
        </div>

        {/* What is it? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Is Flow Complexity?</h2>
          <p className="text-gray-700 mb-4">
            Flow complexity refers to how difficult a flow is to understand and maintain. Complexity
            increases with the number of elements, decision paths, nested loops, and the variety of
            operations performed.
          </p>

          <div className="mt-4 p-4 bg-gray-50 rounded">
            <p className="text-sm font-semibold text-gray-900 mb-2">Signs of High Complexity:</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
              <li>More than 50 elements in a single flow</li>
              <li>Deep nesting (loops within loops, decisions within decisions)</li>
              <li>Multiple different objects being updated</li>
              <li>Complex logic that requires extensive comments to understand</li>
              <li>Difficulty viewing the entire flow on one screen</li>
              <li>New team members struggle to understand what the flow does</li>
            </ul>
          </div>
        </div>

        {/* Why is it a problem? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Is High Complexity Problematic?</h2>

          <div className="space-y-4">
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold text-gray-900">Difficult to Debug</h3>
              <p className="text-gray-700 mt-1">
                When a complex flow fails, it's hard to identify which of the 100+ elements caused
                the issue. Debugging time increases exponentially with complexity.
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900">Hard to Modify Safely</h3>
              <p className="text-gray-700 mt-1">
                Making changes to a complex flow risks breaking existing functionality because it's
                difficult to understand all the interactions and edge cases.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="font-semibold text-gray-900">Knowledge Silos</h3>
              <p className="text-gray-700 mt-1">
                Complex flows often become "owned" by one person who built them. When that person
                leaves, no one else understands how they work.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-900">Performance Degradation</h3>
              <p className="text-gray-700 mt-1">
                Complex flows with many elements, nested loops, and multiple database operations
                run slower and are more likely to hit governor limits.
              </p>
            </div>
          </div>
        </div>

        {/* How to identify */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify Overly Complex Flows</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In FlowDocs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Check the Performance Scorecard - Element Count metric</li>
                <li>Look for "High Complexity" warnings</li>
                <li>Review the Flow Elements section to see the full list</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Complexity Thresholds:</h3>
              <div className="mt-2 space-y-2">
                <div className="flex items-center">
                  <span className="w-32 text-sm font-medium text-gray-700">Simple:</span>
                  <span className="text-sm text-gray-600">1-20 elements</span>
                </div>
                <div className="flex items-center">
                  <span className="w-32 text-sm font-medium text-gray-700">Moderate:</span>
                  <span className="text-sm text-gray-600">21-50 elements</span>
                </div>
                <div className="flex items-center">
                  <span className="w-32 text-sm font-medium text-orange-600">Complex:</span>
                  <span className="text-sm text-orange-600">51-100 elements - consider refactoring</span>
                </div>
                <div className="flex items-center">
                  <span className="w-32 text-sm font-medium text-red-600">Very Complex:</span>
                  <span className="text-sm text-red-600">100+ elements - definitely needs refactoring</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Other Warning Signs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>You can't see the entire flow canvas at once</li>
                <li>You need extensive documentation to explain what it does</li>
                <li>Making changes requires testing dozens of scenarios</li>
                <li>The flow handles 5+ different business processes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* How to reduce complexity */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Reduce Flow Complexity</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">The Solution</h3>
                <p className="mt-2 text-sm text-green-700">
                  Break large flows into smaller, focused flows using subflows, formulas, and the
                  single responsibility principle. Each flow should do one thing well.
                </p>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-gray-900 mb-3">Strategies for Reducing Complexity:</h3>

          <ol className="space-y-6">
            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  1
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Extract Logic to Subflows</h4>
                  <p className="text-gray-700 mt-1">
                    Move repeated or complex logic into reusable subflows.
                  </p>
                  <div className="mt-2 bg-gray-50 p-3 rounded">
                    <p className="text-sm font-semibold text-gray-900">Example:</p>
                    <p className="text-sm text-gray-700 mt-1">
                      Instead of duplicating "send notification email" logic in 5 places, create
                      a "Send Notification" subflow and call it from each location.
                    </p>
                  </div>
                  <div className="mt-2 bg-green-50 p-3 rounded">
                    <p className="text-sm text-green-800">
                      <strong>Benefit:</strong> Changes only need to be made in one place
                    </p>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  2
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Use Formula Fields Instead of Calculations</h4>
                  <p className="text-gray-700 mt-1">
                    Move complex calculations to formula fields on the object rather than
                    implementing them in the flow.
                  </p>
                  <div className="mt-2 bg-gray-50 p-3 rounded text-sm">
                    <p className="font-semibold text-gray-900">Before (in flow):</p>
                    <p className="font-mono text-gray-700 mt-1">
                      Assignment: Calculate total price<br />
                      Decision: Check if discount applies<br />
                      Assignment: Apply discount percentage<br />
                      Assignment: Add tax
                    </p>
                    <p className="font-semibold text-gray-900 mt-3">After (formula field):</p>
                    <p className="font-mono text-gray-700 mt-1">
                      Total_Price__c (formula field handles all logic)
                    </p>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  3
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Split By Business Process</h4>
                  <p className="text-gray-700 mt-1">
                    If one flow handles multiple business processes, split it into separate flows.
                  </p>
                  <div className="mt-2 bg-gray-50 p-3 rounded text-sm">
                    <p className="font-semibold text-gray-900">Example:</p>
                    <p className="text-gray-700 mt-1">
                      Instead of "Opportunity Management Flow" that handles:<br />
                      • Stage changes<br />
                      • Renewal processing<br />
                      • Territory assignment<br />
                      • Commission calculations
                    </p>
                    <p className="text-gray-700 mt-2">
                      Create 4 focused flows, each triggered by the appropriate field change
                    </p>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  4
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Simplify Decision Logic</h4>
                  <p className="text-gray-700 mt-1">
                    Replace nested decisions with formula fields that return boolean values.
                  </p>
                  <div className="mt-2 bg-gray-50 p-3 rounded text-sm">
                    <p className="text-gray-700">
                      Instead of 5 nested Decision elements checking various conditions,
                      create formula fields like:
                    </p>
                    <ul className="list-disc list-inside mt-2 text-gray-700 ml-4">
                      <li>Is_High_Value__c (formula)</li>
                      <li>Needs_Approval__c (formula)</li>
                      <li>Is_Eligible_For_Discount__c (formula)</li>
                    </ul>
                    <p className="text-gray-700 mt-2">
                      Then use simple Decision elements on these boolean fields
                    </p>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  5
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Use Record-Triggered Flows Instead of Scheduled</h4>
                  <p className="text-gray-700 mt-1">
                    Record-triggered flows can be simpler because they work on specific records
                    rather than querying and looping through collections.
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  6
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Add Clear Descriptions and Labels</h4>
                  <p className="text-gray-700 mt-1">
                    Even if you can't reduce elements, make the flow easier to understand with:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 mt-2 space-y-1">
                    <li>Descriptive element labels (not "Decision", but "Check If High Value Customer")</li>
                    <li>Flow description explaining the overall purpose</li>
                    <li>Comments/annotations at key decision points</li>
                    <li>Logical grouping of related elements</li>
                  </ul>
                </div>
              </div>
            </li>
          </ol>
        </div>

        {/* Before/After Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (Monolithic)</h3>
              </div>
              <div className="space-y-2 text-sm bg-gray-50 p-3 rounded">
                <div className="font-semibold text-gray-900">One "Account Management" Flow:</div>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1 text-xs">
                  <li>35 elements for billing address updates</li>
                  <li>28 elements for revenue calculations</li>
                  <li>22 elements for territory assignment</li>
                  <li>18 elements for renewal processing</li>
                  <li>15 elements for commission tracking</li>
                </ul>
                <div className="mt-2 pt-2 border-t font-semibold text-red-700">
                  Total: 118 elements
                </div>
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (Modular)</h3>
              </div>
              <div className="space-y-2 text-sm bg-gray-50 p-3 rounded">
                <div className="font-semibold text-gray-900">Five Focused Flows:</div>
                <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1 text-xs">
                  <li>Billing Address Handler (12 elements)</li>
                  <li>Revenue Calculator (8 elements + formula fields)</li>
                  <li>Territory Assigner (15 elements)</li>
                  <li>Renewal Processor (18 elements)</li>
                  <li>Commission Tracker (10 elements + subflow)</li>
                </ul>
                <div className="mt-2 pt-2 border-t font-semibold text-green-700">
                  Largest: 18 elements
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 p-4 rounded">
            <p className="text-sm text-blue-800">
              <strong>Result:</strong> Each flow is easy to understand, test, and modify independently.
              New team members can quickly grasp what each one does.
            </p>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Complexity Management Best Practices</h2>

          <div className="space-y-4">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Single Responsibility Principle</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Each flow should have one clear purpose. If you need "and" to describe it, split it.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Aim for Under 30 Elements</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Target 20-30 elements per flow. More than 50 should trigger a refactoring review.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Reuse Common Logic</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Create subflows for logic used in multiple places (notifications, lookups, validations)
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Document Your Flows</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Add descriptions to flows and use clear, descriptive element names
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Regular Refactoring</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Periodically review flows and refactor before they become unmanageable
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Benefits</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">Maintainability: Dramatically improved</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Debugging time reduced by 50-75%</li>
              <li>Onboarding new team members 3x faster</li>
              <li>Changes can be made with confidence</li>
              <li>Better test coverage due to focused scope</li>
            </ul>
            <p className="mt-4">
              Well-designed, modular flows are assets that provide value for years. Overly complex
              flows become technical debt that slow down your entire team.
            </p>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to building high-quality flows</p>
            </li>
            <li>
              <Link to={`${basePath}/help/error-handling`} className="text-blue-600 hover:text-blue-800 font-medium">
                Error Handling →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Making flows more robust and debuggable</p>
            </li>
            <li>
              <Link to={`${basePath}/help/bulkification`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Bulkification →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Processing multiple records efficiently</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
